/**
 * Hand-written Server-Sent Events (SSE) client for the chat send endpoint.
 *
 * `POST /api/chat/conversations/{id}/messages` streams its reply as
 * `text/event-stream` — the assistant reply arrives token-by-token with
 * tool-call activity in between and a final event carrying the persisted
 * messages. openapi-ts cannot model a streaming endpoint, so this one
 * endpoint is driven by a bespoke `fetch()` + stream reader instead of the
 * generated SDK. Every OTHER endpoint keeps using the generated client.
 *
 * Transport notes:
 *   - EventSource can't POST multipart, so we use `fetch()` with a `FormData`
 *     body and read `response.body.getReader()`.
 *   - `credentials: "include"` sends the HTTP-only session cookie.
 *   - The base path (`/api`) matches how the generated client is configured
 *     in `./client` — Vite (dev) / nginx (prod) proxy it to the backend, so
 *     the browser never learns the backend URL.
 */

import type { ChatConversationSummary, ChatMessage } from "@/lib/api/generated";

/** Same `/api` prefix the generated @hey-api client targets (see ./client). */
const API_BASE_PATH = "/api";

/** Payload of the first `meta` event — render the user's turn immediately. */
export interface ChatStreamMeta {
  conversation_id: string;
  user_message: ChatMessage;
}

/** A retrieval step started (e.g. `name: "search_bom"`). */
export interface ChatStreamToolUse {
  id: string;
  name: string;
  input: Record<string, unknown>;
}

/** A retrieval step finished. */
export interface ChatStreamToolResult {
  id: string;
  name: string;
  summary: string;
  ok: boolean;
}

/** Final persisted assistant message plus the updated conversation header. */
export interface ChatStreamDone {
  assistant_message: ChatMessage;
  conversation: ChatConversationSummary;
}

/** Callbacks fired as the stream is parsed. All are optional. */
export interface ChatStreamHandlers {
  /** First event — carries the persisted user message. */
  onMeta?: (meta: ChatStreamMeta) => void;
  /** Assistant prose token delta — append to the in-progress bubble. */
  onText?: (delta: string) => void;
  /** A tool/retrieval step started. */
  onToolUse?: (tool: ChatStreamToolUse) => void;
  /** A tool/retrieval step finished. */
  onToolResult?: (tool: ChatStreamToolResult) => void;
  /** Final event — the authoritative persisted assistant message. */
  onDone?: (done: ChatStreamDone) => void;
  /** Backend-emitted error event. */
  onError?: (detail: string) => void;
}

/** Body for a streamed send. At least one of `content` / `files` is required. */
export interface ChatStreamRequest {
  conversationId: string;
  content?: string;
  files?: File[];
  /**
   * A trusted, explicit machine choice from a candidate click — sets the
   * conversation's machine directly, bypassing fuzzy text resolution.
   */
  machineId?: number;
}

/** A single parsed SSE frame (`event:` + `data:` lines). */
interface SseFrame {
  event: string;
  data: string;
}

/**
 * Parse a chunk of raw SSE text into complete frames, returning the frames and
 * whatever trailing partial text is left in the buffer. Frames are separated by
 * a blank line (`\n\n`); a frame may carry multiple `data:` lines which are
 * joined with newlines per the SSE spec.
 */
function drainFrames(buffer: string): { frames: SseFrame[]; rest: string } {
  const frames: SseFrame[] = [];
  // Normalise CRLF so the blank-line delimiter check is simple.
  const normalised = buffer.replace(/\r\n/g, "\n");
  const segments = normalised.split("\n\n");
  // The last segment may be an incomplete frame — keep it in the buffer.
  const rest = segments.pop() ?? "";

  for (const segment of segments) {
    if (segment.trim().length === 0) {
      continue;
    }
    let event = "message";
    const dataLines: string[] = [];
    for (const line of segment.split("\n")) {
      if (line.startsWith("event:")) {
        event = line.slice("event:".length).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice("data:".length).replace(/^ /, ""));
      }
      // `:`-prefixed comment lines and unknown fields are ignored.
    }
    frames.push({ event, data: dataLines.join("\n") });
  }

  return { frames, rest };
}

/** Dispatch one parsed frame to the matching handler. */
function dispatchFrame(frame: SseFrame, handlers: ChatStreamHandlers): void {
  let payload: unknown;
  try {
    payload = frame.data.length > 0 ? JSON.parse(frame.data) : undefined;
  } catch {
    // A malformed data line for one frame shouldn't kill the whole stream.
    return;
  }

  switch (frame.event) {
    case "meta":
      handlers.onMeta?.(payload as ChatStreamMeta);
      break;
    case "text":
      handlers.onText?.((payload as { text: string }).text);
      break;
    case "tool_use":
      handlers.onToolUse?.(payload as ChatStreamToolUse);
      break;
    case "tool_result":
      handlers.onToolResult?.(payload as ChatStreamToolResult);
      break;
    case "done":
      handlers.onDone?.(payload as ChatStreamDone);
      break;
    case "error":
      handlers.onError?.((payload as { detail: string }).detail);
      break;
    default:
      // Unknown event types are ignored so the contract can grow additively.
      break;
  }
}

/**
 * Open the SSE stream for a chat send and drive `handlers` until it completes.
 *
 * Resolves when the stream ends (server closed the body). Rejects on a
 * transport/HTTP failure; a backend-emitted `error` event is delivered through
 * `handlers.onError` rather than rejecting. Pass `signal` to abort in flight.
 */
export async function streamChatMessage(
  request: ChatStreamRequest,
  handlers: ChatStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const form = new FormData();
  if (request.content !== undefined && request.content.length > 0) {
    form.append("content", request.content);
  }
  for (const file of request.files ?? []) {
    form.append("files", file);
  }
  if (request.machineId !== undefined) {
    form.append("machine_id", String(request.machineId));
  }

  const response = await fetch(
    `${API_BASE_PATH}/chat/conversations/${request.conversationId}/messages`,
    {
      method: "POST",
      body: form,
      credentials: "include",
      headers: { Accept: "text/event-stream" },
      signal,
    },
  );

  if (!response.ok || !response.body) {
    // Surface FastAPI's `{ detail }` when present, else a generic message.
    let detail = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { detail?: string };
      if (body?.detail) {
        detail = body.detail;
      }
    } catch {
      // Non-JSON error body — keep the status-based message.
    }
    const error = new Error(detail);
    handlers.onError?.(detail);
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const { frames, rest } = drainFrames(buffer);
      buffer = rest;
      for (const frame of frames) {
        dispatchFrame(frame, handlers);
      }
    }
    // Flush any final frame left in the buffer (stream may end without a
    // trailing blank line).
    buffer += decoder.decode();
    if (buffer.trim().length > 0) {
      const { frames } = drainFrames(`${buffer}\n\n`);
      for (const frame of frames) {
        dispatchFrame(frame, handlers);
      }
    }
  } finally {
    reader.releaseLock();
  }
}
